/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import { useEffect, useRef, RefObject, useState } from 'react';
import { throttle } from 'lodash-es';
// import axios from 'axios';
// import { API_ROOT } from 'utils/constants/urls';
import { logError } from 'utils/helpers';

export const THROTTLE = 200;

/**
 * attaches and removes throttled scroll event listener to execute a callback once
 * the element associated with the ref parameter has come 1/5 of the way into the page
 * @param action function
 * @param ref RefObject
 */
function useScrolledIntoViewOnce(
  action: Function,
  ref?: RefObject<HTMLDivElement>,
  offset = 0,
): RefObject<HTMLDivElement> {
  const containerEl = ref || useRef<HTMLDivElement>(null);

  const isInView: () => boolean = () => {
    if (!containerEl.current) {
      logError(
        `Can't determine if object is in view. Something is wrong with the ref argument.`,
        {
          component: 'hooks',
          function: 'useScrolledIntoViewOnce -> isInView',
        },
      );
      return false;
    }

    const windowHeight = window.innerHeight;
    const isScrolled = window.pageYOffset > 0;

    const { top: containerTop } = containerEl.current.getBoundingClientRect();

    const vertInView = windowHeight - containerTop + offset > windowHeight / 7;

    return (containerTop > 0 && isScrolled && vertInView) || vertInView;
  };

  const handleScroll: () => void = () => {
    if (isInView()) {
      action();
      stopListening();
    }
  };

  const handleScrollThrottled = throttle(handleScroll, THROTTLE);

  const stopListening: () => void = () => {
    window.removeEventListener('scroll', handleScrollThrottled);
  };

  useEffect(() => {
    if (isInView()) {
      action();
      return;
    }
    window.addEventListener('scroll', handleScrollThrottled);
    return stopListening;
  }, []);

  return containerEl;
}

/**
 * attaches throttled scroll event listener to return boolean when the ref has entered 1/5 or if bottom of element is in view
 * @param ref RefObject
 */
function useScrolledIntoView(
  ref: RefObject<HTMLDivElement>,
  threshold?: number,
): { ref: RefObject<HTMLDivElement>; inView: boolean } {
  const containerEl = ref || useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState<boolean>(false);

  const isInView: () => boolean = () => {
    if (!containerEl || !containerEl.current) {
      logError(
        `Can't determine if object is in view. Something is wrong with the ref argument.`,
        {
          component: 'hooks',
          function: 'useScrolledIntoView -> isInView',
        },
      );
      return false;
    }

    const windowHeight = window.innerHeight;
    const isScrolled = window.pageYOffset > 0;

    const {
      top: containerTop,
      bottom: containerBottom,
    } = containerEl.current.getBoundingClientRect();

    const vertInView = threshold
      ? windowHeight - containerTop > windowHeight + threshold
      : windowHeight - containerTop > windowHeight / 5;
    const bottomInView = containerBottom <= windowHeight;
    const isInView = isScrolled && (vertInView || bottomInView);

    return isInView;
  };

  const handleScroll: () => void = () => {
    if (isInView()) {
      setInView(true);
    } else {
      setInView(false);
    }
  };

  const handleScrollThrottled = throttle(handleScroll, THROTTLE);

  const stopListening: () => void = () => {
    window.removeEventListener('scroll', handleScrollThrottled);
  };

  useEffect(() => {
    if (isInView()) {
      setInView(true);
      return;
    }
    window.addEventListener('scroll', handleScrollThrottled);
    return stopListening;
  }, []);

  return {
    ref: containerEl,
    inView,
  };
}

function useMembershipProducts(userData: any, productsBySlug: any): any {
  if (userData.isMember === true) {
    const controlMembershipVariants = {
      'chlamydia-gonorrhea-test': 505,
      'syphilis-test': 506,
      'hepatitis-c-test': 507,
      'hiv-test': 508,
      'trichomoniasis-test': 509,
      hba1c: 510,
      'cholesterol-and-lipids-test': 511,
      'thyroid-test': 512,
      metabolism: 513,
      'testosterone-test': 514,
      'vitamin-d-test': 515,
      'vitamin-d-and-inflammation-test': 516,
      'fit-colon-cancer-screening-test': 517,
      'hpv-test-female': 518,
    };

    const stiMembershipVariants = {
      'chlamydia-gonorrhea-test': 375,
      'syphilis-test': 376,
      'hepatitis-c-test': 377,
      'hiv-test': 378,
      'trichomoniasis-test': 379,
    };

    let membershipVariants = {};
    if (userData.membershipType === 'control') {
      membershipVariants = controlMembershipVariants;
    } else if (userData.membershipType === 'sti') {
      membershipVariants = stiMembershipVariants;
    }

    Object.keys(productsBySlug).map((key: any) => {
      if (
        Object.keys(membershipVariants).includes(productsBySlug[key].slug) &&
        userData.activeCredits > 0
      ) {
        productsBySlug[key].memberPrice = `Free`;
        productsBySlug[key].memberHeroCopy = 'Included with Membership';
        productsBySlug[key].promoText = 'Included with Membership';
        productsBySlug[key].activeCredits = userData.activeCredits;
        productsBySlug[key].membershipVariantId =
          //@ts-ignore
          membershipVariants[productsBySlug[key].slug];
      } else if (productsBySlug[key].slug !== 'covid-19-test') {
        const memberPrice = parseFloat(productsBySlug[key].price) * 0.8;
        productsBySlug[key].memberPrice = `$${memberPrice.toFixed(0)}`;
        productsBySlug[key].memberHeroCopy = 'Member Price (20% Off)';
      }
    });
  }
  return productsBySlug;
}

export { useScrolledIntoViewOnce, useScrolledIntoView, useMembershipProducts };
